import type { QueryParams } from '@/lib/api/client';

/**
 * Centralized query-key factory (plan §4.4). Every key is [domain, scope, ...params] so
 * mutations can invalidate precise families. Lists embed their params for cache separation.
 */
export const keys = {
  me: () => ['me'] as const,
  capabilities: () => ['capabilities'] as const,
  dashboard: () => ['dashboard'] as const,
  users: {
    all: () => ['users'] as const,
    list: (p?: QueryParams) => ['users', 'list', p ?? {}] as const,
  },
  serviceAccounts: {
    all: () => ['service-accounts'] as const,
    list: (p?: QueryParams) => ['service-accounts', 'list', p ?? {}] as const,
  },
  apiTokens: {
    all: () => ['api-tokens'] as const,
    list: (p?: QueryParams) => ['api-tokens', 'list', p ?? {}] as const,
    options: () => ['api-tokens', 'options'] as const,
  },
  oauth: {
    request: (id: string) => ['oauth', 'request', id] as const,
    connectedApps: {
      all: () => ['oauth', 'connected-apps'] as const,
      list: (p?: QueryParams) => ['oauth', 'connected-apps', 'list', p ?? {}] as const,
    },
    connectInfo: () => ['oauth', 'connect-info'] as const,
    grants: {
      all: () => ['oauth', 'grants'] as const,
      list: (p?: QueryParams) => ['oauth', 'grants', 'list', p ?? {}] as const,
    },
    clients: {
      all: () => ['oauth', 'clients'] as const,
      list: (p?: QueryParams) => ['oauth', 'clients', 'list', p ?? {}] as const,
    },
  },
  blocks: { schemas: () => ['blocks', 'schemas'] as const },
  collectionSchemas: () => ['collection-schemas'] as const,
  pages: {
    all: () => ['pages'] as const,
    list: (p?: QueryParams) => ['pages', 'list', p ?? {}] as const,
    detail: (id: string) => ['pages', 'detail', id] as const,
    sections: (id: string) => ['pages', 'sections', id] as const,
    /** The site map + every page's regions (GET /admin/page-outlines). */
    outlines: () => ['pages', 'outlines'] as const,
  },
  families: {
    all: () => ['families'] as const,
    list: (p?: QueryParams) => ['families', 'list', p ?? {}] as const,
    detail: (id: string) => ['families', 'detail', id] as const,
  },
  content: {
    all: (collection: string) => ['content', collection] as const,
    list: (collection: string, p?: QueryParams) =>
      ['content', collection, 'list', p ?? {}] as const,
    detail: (collection: string, id: string) => ['content', collection, 'detail', id] as const,
    /** The whole collection (every page), for reorder mode; `placement` scopes it to one surface. */
    order: (collection: string, placement?: string) =>
      ['content', collection, 'order', placement ?? ''] as const,
  },
  media: {
    all: () => ['media'] as const,
    list: (p?: QueryParams) => ['media', 'list', p ?? {}] as const,
    detail: (id: string) => ['media', 'detail', id] as const,
    usages: (id: string) => ['media', 'usages', id] as const,
  },
  leads: {
    all: (type: string) => ['leads', type] as const,
    lists: (type: string) => ['leads', type, 'list'] as const,
    list: (type: string, p?: QueryParams) => ['leads', type, 'list', p ?? {}] as const,
    detail: (type: string, id: string) => ['leads', type, 'detail', id] as const,
  },
  revisions: (entityType: string, entityId: string) => ['revisions', entityType, entityId] as const,
  audit: (p?: QueryParams) => ['audit', p ?? {}] as const,
  outbox: {
    all: () => ['outbox'] as const,
    list: (p?: QueryParams) => ['outbox', 'list', p ?? {}] as const,
  },
  settings: {
    doc: () => ['settings', 'doc'] as const,
    /** The public `GET /settings` (any signed-in role): the site-wide default share image. */
    publicDoc: (locale: string) => ['settings', 'public', locale] as const,
    strings: () => ['settings', 'strings'] as const,
    navigation: (key: string) => ['settings', 'navigation', key] as const,
    redirects: (p?: QueryParams) => ['settings', 'redirects', p ?? {}] as const,
    contactDirectory: () => ['settings', 'contact-directory'] as const,
    countries: () => ['settings', 'countries'] as const,
    pageCopy: () => ['settings', 'page-copy'] as const,
    pageCopyDetail: (ns: string) => ['settings', 'page-copy', ns] as const,
    pageCopyUsages: (ns: string) => ['settings', 'page-copy', ns, 'usages'] as const,
  },
  search: (q: string, locale: string) => ['search', q, locale] as const,
} as const;
